import * as React from "react";

import * as R from "ramda";

import GenButton from "../Gen/Button";
import GenModal from "../Gen/Modal";

import {
  deepFindByUID,
  setNodeValueByUIDAndKey,
} from "../../src/doc_helpers";

interface KeyMetadata {
  key: keyof OpenAiChatNode;
  inputType: "textarea" | "textinput" | "checkbox";
  label: string;
  extraExplination?: string;
}


const EDIT_METADATA_LOOKUP_BY_TYPE = {
  openAiChat: [
    {
      key: "aiInstructions",
      inputType: "textarea",
      label: "AI Prompt",
      description: "To add a customizable field to the text use the format [[[Custom Name]]] or [[[Custom Name|Default text]]] to include default text",
    },
    {
      key: "assistantDisplayName",
      inputType: "textinput",
      label: "AI Display Name",
    },
    {
      key: "assistantDisplayImage",
      inputType: "textinput",
      label: "AI Display Image",
      extraExplination: "Use a file name for an image uploaded to the case this activity is used in. If blank then display name will be used.",
    },
    {
      key: "userDisplayName",
      inputType: "textinput",
      label: "User Display Name",
    },
    {
      key: "userDisplayImage",
      inputType: "textinput",
      label: "User Display Image",
      extraExplination: "Use a file name for an image uploaded to the case this activity is used in. If blank then display name will be used.",
    },
    {
      key: "placeholderText",
      inputType: "textinput",
      label: "Placeholder Text",
    },
    {
      key: "startWithAssistant",
      inputType: "checkbox",
      label: "Start with AI",
    },
  ],
} as const;

interface DocEdtMetadataProps {
  editState: EditState;
  currentDocument: CurrentDocument;
  currentVersion: CurrentVersion;
  currentUser: CurrentUser;
  currentRoleDefinition: CurrentRoleDefinition;
  rootNode: BlockContent;
}

export default function DocEditMetadataModal(props: DocEdtMetadataProps) {
  const {
    editState,
    rootNode,
    currentVersion,
  } = props;

  if (!editState.isEditor) { return; }

  const {
    editMetadataUid,
    setEditMetadata,
  } = editState;

  if (!editMetadataUid) { return; }

  const editMetadataNode = deepFindByUID(rootNode, editMetadataUid);

  if (!editMetadataNode || editMetadataNode.type !== "openAiChat") { return; }

  const metadataInputs = EDIT_METADATA_LOOKUP_BY_TYPE[editMetadataNode.type].map((keyMetadata) => {
    let inputInput;
    switch (keyMetadata.inputType) {
      case "textarea":
        inputInput = (
          <textarea
            rows={10} value={editMetadataNode[keyMetadata.key] || ""} onChange={(e) => {
            currentVersion._actions.modifyEditJSON((json) => {
              return setNodeValueByUIDAndKey(json, editMetadataUid, keyMetadata.key, e.target.value);
            });}}
          />
        );

        break;
      case "textinput":
        inputInput = (
          <input
            type="text" value={editMetadataNode[keyMetadata.key] || ""} onChange={(e) => {
            currentVersion._actions.modifyEditJSON((json) => {
              return setNodeValueByUIDAndKey(json, editMetadataUid, keyMetadata.key, !e.target.value || R.isEmpty(e.target.value) ? "" : e.target.value);
            });}}
          />
        );

        break;
      case "checkbox":
        inputInput = (
          <input
            type="checkbox" checked={!!editMetadataNode.startWithAssistant} onChange={(e) => {
            currentVersion._actions.modifyEditJSON((json) => {
              return setNodeValueByUIDAndKey(json, editMetadataUid, "startWithAssistant", e.target.checked);
            });}}
          />
        );

        break;
      default:
        break;
    }

    return (
      <div key={keyMetadata.key}>
        <label>{keyMetadata.label}:</label>
        {"description" in keyMetadata ? <p>{keyMetadata.description}</p> : ""}
        {"extraExplination" in keyMetadata && <p>{keyMetadata.extraExplination}</p>}
        {inputInput}
      </div>
    );
  });

  return (
    <GenModal open>
      <div className="gen-modal-panel">
        <h1>Settings for {editMetadataNode.type}</h1>

        <div>
          <label>UID: {editMetadataNode.uid}</label>
        </div>

        {metadataInputs}

        <GenButton
          name="Close"
          highlighted
          onClick={() => editState.setEditMetadata(undefined)}
        />
      </div>
    </GenModal>
  );
}
